import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { Button, SvgIconHandler } from "../../atoms";
import { STATIC_KEYWORDS } from "../../../constants";
import { SocialShareTemplate } from "../../../arrow";
import { SocialShare } from "@quintype/components";
import { useDeviceType } from "../../customHooks/useDeviceType";
import { TableView } from "./table-view";
import { YearDropdown } from "../mpw-detail-page/year-drop-down";
import { RankingColumns, RankingSubColumns } from "./ranking-columns";
import SearchIcon from "../rankings-table/search";
import { bestBSchoolsStaticData } from "./static-info";
import ListingAdSlot from "../../molecules/ListingAdSlot";
import { TOP100_BILLIONAIRES_DESCRIPTIONS } from "../../../../../config/entity-ranking-descriptions";
import { trackLoadMoreClick } from "../../utils";

import "./ranking-table.m.css";

export const top100Billionaires = ({ data }) => {
  let RankingColumnsFiels = RankingColumns;
  const [columns, setColumns] = useState(RankingColumnsFiels);

  const { staticData, rankingEntitySlug, entitiesByYearJson, activeYear, snapshotByRank } = data;

  const { label, top15Label } = staticData;
  const description = TOP100_BILLIONAIRES_DESCRIPTIONS[activeYear];
  const { LOAD_MORE, READ_MORE, NO_RESULT_FOUND, TRY_AGAIN } = STATIC_KEYWORDS;
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const isAdfreePage = get(qtState, ["config", "subscriber"]) === "true";
  const isWebView = get(qtState, ["config", "isWebView"]) === true;

  const [enableLoadMore, setEnableLoadMore] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [readMoreClicked, setReadMoreClicked] = useState(false);
  const [yearDropdownActive, setYearDropdownActive] = useState(false);
  const [visibleItemsForList, setVisibleItemsForList] = useState(10);
  const [showEntities, setShowEntities] = useState(snapshotByRank.snapshots);
  const [searchCount, setSearchCount] = useState(snapshotByRank.snapshots.length);
  const [fullUrl, setFullUrl] = useState("");
  const [currentView, setCurrentView] = useState("overall");
  const [datum, setDatum] = useState(snapshotByRank);
  const [loading, setLoading] = useState(true);
  const [activeObj, setActiveObj] = useState({});

  const subheadlineRef = useRef(null);
  const searchInputRef = useRef(null);
  const prevSearchTextRef = useRef(searchText);

  const expandBio = () => setReadMoreClicked(true);

  // Track B-Schools page interactions
  let isTracking = false;
  const trackRankingInteraction = (action, value, position = null) => {
    if (isTracking) return;
    isTracking = true;
    setTimeout(() => {
      isTracking = false;
    }, 300);
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({
        event: "dl_ranking_detail_interactions",
        event_action: action,
        link_text: value,
        ...(position && { link_position: position }),
        category: "rankings",
        sub_category: rankingEntitySlug || "NA",
        template_name: "rankdetail",
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const searchTerm = searchInputRef.current.value;
    setSearchText(searchTerm);
    if (searchTerm) {
      trackRankingInteraction("ranking_search", searchTerm.toLowerCase());
    }
  };

  const handleSort = (column) => {
    trackRankingInteraction("sort_applied", column?.name?.toLowerCase());
    const sortedEntities = [...snapshotByRank].sort((a, b) => {
      const aValue = isNaN(parseFloat(a[column.value])) ? -Infinity : parseFloat(a[column.value]);
      const bValue = isNaN(parseFloat(b[column.value])) ? -Infinity : parseFloat(b[column.value]);
      if (aValue > bValue) return column.name.toLowerCase() === "overall" ? 1 : -1;
      if (aValue < bValue) return column.name.toLowerCase() === "overall" ? -1 : 1;
    });
    setShowEntities(sortedEntities);
  };

  const handleRestoreColumn = (c) => () => {
    setColumns((prevColumns) =>
      prevColumns.map((column) => (column.value === c.value ? { ...column, show: !column.show } : column))
    );
  };

  const getMoreStories = () => {
    trackLoadMoreClick({
      linkCategory: `${rankingEntitySlug}-${activeYear}`,
      category: "ranking",
      subCategory: rankingEntitySlug,
      templateName: "rankdetail",
    });
    setVisibleItemsForList((prev) => prev + visibleItemsForList);
    trackRankingInteraction("load_more", "load_more");
  };

  const startsWithCaseInsensitive = (str, prefix) => str.toLowerCase().startsWith(prefix.toLowerCase());

  useEffect(() => {
    setVisibleItemsForList(10);
    if (typeof window !== "undefined") {
      setFullUrl(window.location.href);
    }
  }, []);

  useEffect(() => {
    setEnableLoadMore(visibleItemsForList < snapshotByRank.snapshots.length);
  }, [visibleItemsForList]);

  useEffect(() => {
    if (!isDesktop && subheadlineRef.current) {
      const bioHeight = window?.getComputedStyle(subheadlineRef.current);
      const getLineHeight = parseFloat(bioHeight.lineHeight);
      const lineHeightForThree = getLineHeight * 3;
      const contentHeight = subheadlineRef.current.scrollHeight;
      subheadlineRef.current.style.height = `${lineHeightForThree}px`;
      subheadlineRef.current.style.overflow = "hidden";

      if (readMoreClicked) {
        subheadlineRef.current.style.height = `${contentHeight}px`;
      }
    }
  }, [readMoreClicked, isDesktop]);

  useEffect(() => {
    //const isGroupedView = currentView === "zone" || currentView === "city";
    const isGroupedView = false;
    const entities = datum.snapshots;

    if (searchText) {
      const filterFn = (snapshot) => startsWithCaseInsensitive(snapshot.name, searchText);

      if (isGroupedView) {
        setShowEntities(
          entities
            .map((item) => ({
              ...item,
              snapshots: item.snapshots.filter(filterFn),
            }))
            .filter((item) => item.snapshots.length > 0)
        );
      } else {
        const filteredSnapshots = entities.filter(filterFn);
        setSearchCount(filteredSnapshots.length);
        setShowEntities(filteredSnapshots);
      }
    } else if (prevSearchTextRef.current) {
      setShowEntities(entities);
    }

    prevSearchTextRef.current = searchText;
  }, [searchText]);

  const fetchData = async (group) => {
    try {
      setLoading(true);

      const fetchJson = async (url) => {
        const response = await fetch(url);
        return response.json();
      };

      return group.urls.length === 1 ? await fetchJson(group.urls[0]) : await Promise.all(group.urls.map(fetchJson));
    } catch (err) {
      console.error(err);
    }
  };

  const setCurrentViewFunc = async (view) => {
    setLoading(true);
    setCurrentView(view);
    trackRankingInteraction("view_change", view.toLowerCase());
  };

  useEffect(() => {
    if (!yearDropdownActive || isDesktop) {
      document.body.classList.remove("no-scroll");
    } else if (yearDropdownActive && !isDesktop) {
      document.body.classList.add("no-scroll");
    }
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [yearDropdownActive]);

  useEffect(() => {
    setSearchText("");
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }

    const activeViewObj = bestBSchoolsStaticData(activeYear).find((obj) => obj.key === currentView);
    setActiveObj(activeViewObj);

    if (currentView === "overall") {
      setDatum(snapshotByRank);
      setShowEntities(snapshotByRank?.snapshots);
      setLoading(false);
      return;
    }

    const fetchAndSetData = async () => {
      const data = await fetchData(activeViewObj);
      setDatum(data);
      setShowEntities(data);
      setLoading(false);
    };

    fetchAndSetData();
  }, [currentView]);

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "dl_page_info",
      cms_author_name: "NA",
      byline: "NA",
      category: "rankings",
      sub_category: "bestbschools",
      content_id: "NA",
      template_name: "rankdetail",
      publish_date: "NA",
      publish_time: "NA",
      login_status: "not-logged-in", // will be changed
      subscription_status: "not-subscribed", // will be changed
      ad_free: false, // will be changed
      login_wall_enabled: false,
      platform: window.innerWidth >= 768 ? "Desktop" : "Mobile",
    });
  }, []);

  const renderTableView = (
    top100BillionairesList,
    title = null,
    columns = RankingColumns,
    subColumns = RankingSubColumns
  ) => (
    <div styleName={`page-body`}>
      {title && <h2>{title.toLowerCase()}</h2>}
      <TableView
        top100BillionairesList={top100BillionairesList}
        visibleItemsForList={visibleItemsForList}
        rankingEntitySlug={rankingEntitySlug}
        columns={columns}
        currentView={currentView}
        subcolumns={subColumns}
        trackClick={(item, viewType) =>
          trackRankingInteraction(
            viewType === "grid" ? "grid_item_click" : "list_item_click",
            item.name || item.company,
            item.rank
          )
        }
      />
    </div>
  );

  return (
    <div styleName="entity-detail-page">
      <div
        styleName="banner"><img src={!isDesktop ? "/images/banners/billionaires-mobile.png" : "/images/banners/billionaires-desktop.png"} alt={top15Label} title={top15Label} loading="lazy" width={1470} height={417} /></div>
      <div className="container" styleName="content-wrapper">
        <div styleName="header">
          <div styleName="header-content">
            <div styleName="headline">
              <span styleName="yeardrop">
                <YearDropdown
                  isDesktop={isDesktop}
                  yearDropdownActive={yearDropdownActive}
                  setYearDropdownActive={setYearDropdownActive}
                  yearData={[2025]}
                  rankingEntitySlug={rankingEntitySlug}
                  entitiesByYearJson={entitiesByYearJson}
                  page="top-billionaires"
                  activeYear={activeYear}
                  onYearChange={(year) => trackRankingInteraction("year_filter", year)}
                />
              </span>
              <h1 styleName="text">{`${label || top15Label} ${activeYear}`}</h1>
            </div>
            <div styleName="sub-headline clamped-text" ref={subheadlineRef}>
              {description}
            </div>
            {!readMoreClicked && !isDesktop && (
              <button onClick={expandBio} styleName="read-more">
                {READ_MORE}
              </button>
            )}
          </div>
        </div>
        <div styleName="search-row">
          <form onSubmit={handleSearch}>
            <div styleName="search">
              <div styleName="input">
                {searchText && searchInputRef?.current?.value !== "" && (
                  <span
                    onClick={() => {
                      setSearchText("");
                      searchInputRef.current.value = "";
                    }}
                    styleName="best-Bschools-clearSearch"
                  >
                    <SvgIconHandler type="close" height="12px" width="12px" viewBox="0 0 12 12" />
                  </span>
                )}
                <input
                  type="text"
                  name="search"
                  placeholder="Search"
                  ref={searchInputRef}
                  styleName={searchInputRef?.current?.value !== "" ? "input-bar" : ""}
                />
              </div>
              <Button customStyleName={"bg-brand-primary"}>{isDesktop ? "Search" : <SearchIcon />}</Button>
            </div>
          </form>
          {!isWebView && (
            <div styleName="list-grid-toggle">
              {/* <SortDropdown columns={columns} handleSort={handleSort} isDesktop={isDesktop} /> */}
              <SocialShare
                template={(props) =>
                  SocialShareTemplate({
                    ...props,
                    onShareClick: (platform) =>
                      trackRankingInteraction("share_click", platform?.toLowerCase() || "share"),
                  })
                }
                fullUrl={fullUrl}
                title={`${label} ${activeYear}`}
                size={18}
              />
            </div>
          )}
        </div>
        {!showEntities ||
        showEntities.length === 0 ||
        (showEntities.snapshots && showEntities.snapshots.length === 0) ? (
          <div styleName="oops-text">
            <span>
              {NO_RESULT_FOUND}'{searchText}' <br /> {TRY_AGAIN}
            </span>
          </div>
        ) : (
          <>{currentView === "overall" && renderTableView(showEntities)}</>
        )}

        {enableLoadMore && visibleItemsForList <= searchCount ? (
          <div styleName="load-more-button">
            <Button onClickFunction={() => getMoreStories()}>{LOAD_MORE}</Button>
          </div>
        ) : (
          <div style={{ marginBottom: isDesktop ? "" : "16px" }} />
        )}
        <br />
        <p styleName="footnote">
          <strong>NOTE:</strong>
          <ul>
            <li styleName="footnote">
              Dollar billion valuation for 2025 based on USD/INR exchange rate of 85.76 as on June 30, 2025, while the
              2024 dollar billion value was based on an exchange rate of 83.96 as on August 16, 2024;
            </li>
            <li styleName="footnote">
              In the case of some business houses/individuals, flagship companies refer to largest wealth contributors.
              Owing to space constraints, other wealth contributors could not be included;
            </li>
            <li styleName="footnote">
              *The list features the name of Shiv Nadar as the shareholding period considered for the study was December
              2024. The stake was subsequently transferred to his daughter Roshni Nadar on March 6, 2025;
            </li>
            <li styleName="footnote">
              ** Following the formal split within the Godrej family, brothers Adi-Nadir and brother-sister duo Jamshyd
              Godrej-Smita Godrej Crishna have been listed separately on the list.
            </li>
            <li styleName="footnote">Source: Fortune India Research, Capitaline, BSE, Annual Reports, Tracxn</li>
          </ul>
        </p>
        {!isAdfreePage && (
          <ListingAdSlot
            adType="above_footer"
            pageType="best-b-schools-detail"
            adStyleName="ad-slot-size-728x90-300x250"
            index={3}
          />
        )}
      </div>
    </div>
  );
};

top100Billionaires.propTypes = {
  data: PropTypes.object,
};
